import {
  Button,
  Form,
  Icons,
  message,
  Modal,
  Select,
  Steps,
  Tag,
  Typography,
  Upload,
  UploadFile,
  UploadProps,
  useSelect,
} from '@pankod/refine-antd'
import React from 'react'

import {
  TCertificateCategory,
  TCertificateCategoryActor,
} from '@resources/lms-management/certificate-categories'
import { CreateCertificateTemplatePayload } from '@resources/lms-management/certificate-template/types'

const { Text } = Typography

const NON_TTE_OPTION_VALUE = '__NO_TTE__'

const UPLOAD_FILE_SIZE_LIMIT = 16 * 1024 * 1024 // 16 MB

type CreateTemplateDesignModalProps = {
  visible?: boolean
  onClose?: () => void
  onConfirm?: (
    data: CreateCertificateTemplatePayload,
    file?: File,
  ) => Promise<void>
  unavailableTypes?: { kapabilitas: string; certificateType: string }[]
}

const kapabilitasOptions = [
  {
    label: 'Peserta',
    value: 'peserta',
  },
  {
    label: 'Fasilitator',
    value: 'instruktur',
  },
  {
    label: 'Asisten',
    value: 'asisten',
  },
  {
    label: 'Instruktur Webinar',
    value: 'instruktur_webinar',
  },
]

const certificateTypeOptions = [
  { label: 'Penyelesaian (Lulus)', value: 'COMPLETION' },
  { label: 'Partisipasi (Tidak Lulus)', value: 'PARTICIPATION' },
]

const sortActors = (actors: TCertificateCategoryActor[] = []) =>
  [...actors].sort((left, right) => left.order - right.order)

const getActorTitle = (actor: TCertificateCategoryActor) =>
  actor.name || actor.email || actor.userId

const CreateTemplateDesignModal = ({
  visible,
  onClose,
  onConfirm,
  unavailableTypes,
}: CreateTemplateDesignModalProps) => {
  const [form] = Form.useForm()
  const [fileList, setFileList] = React.useState<UploadFile[]>([])
  const [pendingFile, setPendingFile] = React.useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const { queryResult } = useSelect<TCertificateCategory>({
    resource: 'certificate-categories',
    dataProviderName: 'lms',
    optionLabel: 'name',
    optionValue: 'id',
    queryOptions: {
      enabled: !!visible,
    },
    defaultValueQueryOptions: {
      enabled: false,
    },
    filters: [
      {
        field: 'pageSize',
        operator: 'eq',
        value: -1,
      },
    ],
  })

  const handleCancel = () => {
    form.resetFields()
    setFileList([])
    setPendingFile(null)
    onClose?.()
  }

  const handleBeforeUpload: UploadProps['beforeUpload'] = (file) => {
    if (file.type !== 'application/pdf') {
      message.error('Anda hanya dapat mengunggah file PDF')
      return Upload.LIST_IGNORE
    }
    if (file.size > UPLOAD_FILE_SIZE_LIMIT) {
      message.error('Ukuran file tidak boleh lebih dari 16 MB')
      return Upload.LIST_IGNORE
    }
    setPendingFile(file)
    return false
  }

  const handleFileChange: UploadProps['onChange'] = ({ fileList }) => {
    setFileList(fileList)
  }

  const kapabilitasValue = Form.useWatch('kapabilitas', form)
  const certificateCategoryIdValue = Form.useWatch(
    'certificateCategoryId',
    form,
  )

  const categories = queryResult.data?.data || []
  const categoryOptions = [
    {
      label: 'Tanpa Kategori',
      value: NON_TTE_OPTION_VALUE,
    },
    ...categories.map((category) => ({
      label: category.name,
      value: String(category.id),
      disabled: !category.isActive,
    })),
  ]
  const selectedCategory = categories.find(
    (category) => String(category.id) === certificateCategoryIdValue,
  )
  const selectedActors = selectedCategory
    ? sortActors(selectedCategory.actors)
    : []

  return (
    <Modal
      title="Buat Desain Template Sertifikat"
      visible={visible}
      onCancel={handleCancel}
      onOk={async () => {
        await form.validateFields()
        const values =
          form.getFieldsValue() as CreateCertificateTemplatePayload & {
            certificateCategoryId: string
          }
        setIsSubmitting(true)
        try {
          await onConfirm?.(
            {
              ...values,
              certificateCategoryId:
                values.certificateCategoryId === NON_TTE_OPTION_VALUE
                  ? null
                  : values.certificateCategoryId,
            },
            pendingFile ?? undefined,
          )
          handleCancel()
        } catch {
          message.error('Terjadi kesalahan, silakan coba lagi')
        } finally {
          setIsSubmitting(false)
        }
      }}
      okText="Buat Desain"
      cancelText="Batal"
      confirmLoading={isSubmitting}
      cancelButtonProps={{ disabled: isSubmitting }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ certificateCategoryId: NON_TTE_OPTION_VALUE }}
      >
        <Form.Item
          label="Kapabilitas"
          name="kapabilitas"
          rules={[{ required: true, message: 'Kapabilitas harus diisi' }]}
          dependencies={['certificateType']}
        >
          <Select
            options={kapabilitasOptions}
            placeholder="Pilih kapabilitas"
            allowClear
            showSearch
          />
        </Form.Item>
        <Form.Item
          label="Jenis Sertifikat"
          name="certificateType"
          rules={[{ required: true, message: 'Jenis Sertifikat harus diisi' }]}
          dependencies={['kapabilitas']}
        >
          <Select
            options={certificateTypeOptions.map((item) => ({
              ...item,
              disabled: unavailableTypes?.some(
                (type) =>
                  type.certificateType === item.value &&
                  type.kapabilitas === kapabilitasValue,
              ),
            }))}
            placeholder="Pilih jenis sertifikat"
            allowClear
            showSearch
          />
        </Form.Item>
        <Form.Item
          label="Kategori Sertifikat"
          name="certificateCategoryId"
          rules={[
            { required: true, message: 'Kategori Sertifikat harus diisi' },
          ]}
        >
          <Select
            options={categoryOptions}
            placeholder="Pilih kategori sertifikat"
            allowClear
            showSearch
            loading={queryResult.isFetching}
            onSearch={() => {}}
            filterOption={(input, option) =>
              String(option?.label || '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item label="Dokumen Pendukung (opsional)">
          <Upload
            accept=".pdf"
            beforeUpload={handleBeforeUpload}
            onChange={handleFileChange}
            onRemove={() => {
              setPendingFile(null)
              return true
            }}
            fileList={fileList}
            maxCount={1}
          >
            <Button icon={<Icons.UploadOutlined />} disabled={!!pendingFile}>
              Pilih file PDF
            </Button>
          </Upload>
        </Form.Item>
        {selectedActors.length > 0 ? (
          <div className="rounded border border-neutral-200 px-4 py-3">
            <Text strong className="mb-3 block">
              Detail Approver / Signer
            </Text>
            <Steps direction="vertical" size="small" current={-1}>
              {selectedActors.map((actor) => (
                <Steps.Step
                  key={`${actor.userId}-${actor.order}`}
                  title={getActorTitle(actor)}
                  description={
                    <div className="text-xs leading-5">
                      <div>Jabatan: {actor.jabatan || '-'}</div>
                      <Tag color={actor.role === 'approver' ? 'blue' : 'green'}>
                        {actor.role === 'approver'
                          ? 'Penyetuju (Approver)'
                          : 'Penandatangan (Signer)'}
                      </Tag>
                    </div>
                  }
                />
              ))}
            </Steps>
          </div>
        ) : null}
      </Form>
    </Modal>
  )
}

export default CreateTemplateDesignModal
